import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import { bool, object, string } from "yup";
import { useNavigate } from "react-router-dom";

import {
  callAPIInterface,
  isAlphabet,
  isTMA,
  sourceTelegramApp,
  sourceWebWallet,
} from "../constants";
import {
  agreeMsg,
  fullName,
  invalidName,
  loadingMsg,
  namePlaceHolder,
  registerMessage,
  registerStepTwoHeader,
  registerStepTwoSubHeader,
  termsAndPrivacyMsg,
} from "../messages";
import { setRegisterData } from "../../redux/actions";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import Text from "../text/Text";
import { Input } from "../input/Input";
import InputErrorMessage from "../inputErrorMessage/InputErrorMessage";
import { CustomCheckbox } from "../checkBox/CheckBox";
import { CustomLink } from "../link/Link";
import Button from "../button/Button";
import "../../assets/register.scss";
import SelectCountryDropdown from "../SelectCountryDropdown";
import SelectStateDropdown from "../SelectStateDropdown";

const RegisterStepTwo = ({ isFromOptions = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const registerDetails = useSelector((state) => state.common.registerDetails);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!registerDetails?.email && !registerDetails?.password) {
      navigate(-1);
    }
  }, []);

  const validationSchema = object({
    name: string().required("").matches(isAlphabet, invalidName).trim(),
    country: string().required(),
    state: string().when("statesAvailable", {
      is: true,
      then: () => string().required(),
    }),
    statesAvailable: bool(),
    referralCode: string(),
    promoCode: string(),
    termsAndPrivacy: bool().oneOf([true]),
  });

  const handleRegister = async (val) => {
    try {
      setIsSubmitting(true);
      const stepTwoData = {
        first_name: val?.name?.split(" ")?.[0],
        last_name: val?.name?.split(" ")?.[1] || val?.name?.split(" ")?.[0],
        country: val?.country,
        state: val?.state || undefined,
        referral_code: val?.referralCode || undefined,
        promo_code: val?.promoCode || undefined,
        source: isTMA() ? sourceTelegramApp : sourceWebWallet,
      };
      const data = { ...registerDetails, ...stepTwoData };

      await callAPIInterface("POST", "/signup", JSON.stringify(data));
      dispatch(setRegisterData({ email: "", password: "" }));
      navigate("/verify-email", {
        state: {
          email: registerDetails?.email,
        },
      });
    } catch (error) {
    } finally {
      localStorage.removeItem("referralCode");
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      country: null,
      state: null,
      statesAvailable: false,
      referralCode: "",
      promoCode: "",
      termsAndPrivacy: false,
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: handleRegister,
  });

  const {
    values,
    touched,
    errors,
    setFieldValue,
    isValid,
    dirty,
    handleChange,
    handleSubmit,
    handleBlur,
  } = formik;

  const defaultErrorMessageProps = {
    touched,
    errors,
  };

  return (
    <>
      <BackdropLoader
        open={isSubmitting}
        alt={loadingMsg}
        text={loadingMsg}
        customClass="loading-in-auth"
      />
      <Box
        component="form"
        className="set-elements-vertically"
        onSubmit={handleSubmit}
      >
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          justifyContent="space-between"
          sx={{ gap: 2 }}
        >
          <Box>
            <Text className="default-text " size={22} font="bold">
              {registerStepTwoHeader}
            </Text>
            <Text className="grey-text mt-8" size={14}>
              {registerStepTwoSubHeader}
            </Text>
            <Input
              customClass="mt-24"
              fullWidth
              showLabel
              label={fullName}
              placeholder={namePlaceHolder}
              name="name"
              value={values.name}
              type="text"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && Boolean(errors.email)}
            />
            <InputErrorMessage {...defaultErrorMessageProps} inputName="name" />
            <SelectCountryDropdown
              formik={formik}
              customClass="register-country-input"
            />
            <SelectStateDropdown
              formik={formik}
              customClass="register-country-input"
            />
            {/* <ApplyPromoCode formik={formik} isFromBrowser={isFromOptions} /> */}
            <Box display="flex" alignItems="center" marginY="15px">
              <Box className="checkbox">
                <CustomCheckbox
                  onChange={(_e, checked) =>
                    setFieldValue("termsAndPrivacy", checked)
                  }
                  checked={values.termsAndPrivacy}
                  name="termsAndPrivacy"
                  label=""
                />
              </Box>
              <Box>
                <Text
                  className="grey-text"
                  font="regular"
                  size={14}
                  variant="caption"
                >
                  {agreeMsg[0]}
                  <CustomLink
                    size="small"
                    bold
                    href={process.env.REACT_APP_PROMO_WEBSITE + "terms-of-use"}
                    target="_blank"
                  >
                    {termsAndPrivacyMsg[0]}
                  </CustomLink>
                  {agreeMsg[1]}
                  <CustomLink
                    size="small"
                    bold
                    target="_blank"
                    href={
                      process.env.REACT_APP_PROMO_WEBSITE + "privacy-policy"
                    }
                  >
                    {termsAndPrivacyMsg[1]}
                  </CustomLink>
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box mt="10px">
          <Button
            fullWidth
            sx={!isFromOptions && { m: "14px 20px" }}
            variant="contained"
            label={registerMessage}
            type="submit"
            disabled={!(isValid && dirty)}
            {...(!isFromOptions && { textClass: "font-14" })}
          />
        </Box>
      </Box>
    </>
  );
};

export default RegisterStepTwo;
